/**
 * LandingLocalizationPanel — Admin panel for managing per-locale
 * regionalized landing copy and Mei intro video uploads.
 *
 * Mounted inside the admin shell (routes_admin.py / AdminShell.jsx).
 * Shows a table of 6 locales, each row having:
 *   • Status badge (source / published / not_generated / draft)
 *   • "Regenerate" button — calls POST /api/admin/landing/regionalize/{locale}
 *   • Preview toggle — shows the regionalized copy for that locale
 *   • Video upload zone — POST /api/admin/landing/locale-video/{locale}
 *
 * The "Regenerate all" button triggers all 5 non-English locales at once.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Globe, Loader2, RefreshCw, Upload, Trash2, CheckCircle2, AlertCircle,
  ChevronDown, ChevronUp, Play,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const LOCALE_META = {
  en: { flag: '🇬🇧', name: 'English',    note: 'Source — always live' },
  fr: { flag: '🇫🇷', name: 'Français',   note: 'French literary market' },
  de: { flag: '🇩🇪', name: 'Deutsch',    note: 'DACH book market' },
  es: { flag: '🇪🇸', name: 'Español',    note: 'Spain + LatAm indie scene' },
  pt: { flag: '🇧🇷', name: 'Português',  note: 'Brazilian indie romance market' },
  zh: { flag: '🇨🇳', name: '中文',        note: 'Chinese online literature + diaspora' },
};

const STATUS_CONFIG = {
  source:        { label: 'Source',        color: 'bg-sky-100 text-sky-800 border-sky-200' },
  published:     { label: 'Published',     color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  draft:         { label: 'Draft',         color: 'bg-amber-100 text-amber-800 border-amber-200' },
  not_generated: { label: 'Not generated', color: 'bg-secondary text-muted-foreground border-border' },
};

export default function LandingLocalizationPanel() {
  const [locales, setLocales]         = useState([]);
  const [videos, setVideos]           = useState({});
  const [loading, setLoading]         = useState(true);
  const [generatingAll, setGenAll]    = useState(false);
  const [generating, setGenerating]   = useState({});
  const [uploading, setUploading]     = useState({});
  const [expanded, setExpanded]       = useState({});
  const [preview, setPreview]         = useState({});
  const fileRefs                      = useRef({});

  const load = async () => {
    try {
      const [lR, vR] = await Promise.all([
        axios.get(`${API}/admin/landing/regionalizations`, { withCredentials: true }),
        axios.get(`${API}/admin/landing/locale-videos`,    { withCredentials: true }),
      ]);
      setLocales(lR.data.locales || []);
      const vMap = {};
      for (const v of (vR.data.videos || [])) vMap[v.locale] = v;
      setVideos(vMap);
    } catch { toast.error('Failed to load localization data.'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const regenerate = async (locale) => {
    setGenerating(p => ({ ...p, [locale]: true }));
    try {
      const r = await axios.post(
        `${API}/admin/landing/regionalize/${locale}`, {},
        { withCredentials: true },
      );
      toast.success(
        `${LOCALE_META[locale]?.name ?? locale} regionalized — ${r.data.generated} fields updated.`,
      );
      await load();
      // Auto-expand the preview after generation.
      setExpanded(p => ({ ...p, [locale]: true }));
    } catch (e) {
      toast.error(`Regionalization failed for ${locale}.`, {
        description: e?.response?.data?.detail || 'Try again in a moment.',
      });
    }
    setGenerating(p => ({ ...p, [locale]: false }));
  };

  const regenerateAll = async () => {
    setGenAll(true);
    try {
      const r = await axios.post(
        `${API}/admin/landing/regionalize-all`, {},
        { withCredentials: true },
      );
      toast.success(
        `All locales regionalized — ${r.data.total_ok}/${r.data.total} succeeded.`,
      );
      await load();
    } catch (e) {
      toast.error('Bulk regionalization failed.', {
        description: e?.response?.data?.detail || 'Check backend logs.',
      });
    }
    setGenAll(false);
  };

  const loadPreview = async (locale) => {
    if (preview[locale]) return; // already loaded
    try {
      const r = await axios.get(
        `${API}/landing/localized-content?locale=${locale}`,
        { withCredentials: true },
      );
      setPreview(p => ({ ...p, [locale]: r.data.content || {} }));
    } catch { /* show nothing */ }
  };

  const toggleExpand = (locale) => {
    const next = !expanded[locale];
    setExpanded(p => ({ ...p, [locale]: next }));
    if (next) loadPreview(locale);
  };

  const deleteLocale = async (locale) => {
    if (!window.confirm(`Delete regionalized content for ${locale}?`)) return;
    await axios.delete(`${API}/admin/landing/regionalizations/${locale}`, { withCredentials: true });
    toast.info(`${locale} regionalization cleared.`);
    await load();
    setPreview(p => ({ ...p, [locale]: undefined }));
  };

  const uploadVideo = async (locale, file) => {
    if (!file) return;
    const allowed = ['video/mp4', 'video/webm', 'video/quicktime'];
    if (!allowed.includes(file.type)) {
      toast.error('Only MP4, WebM, or MOV files are accepted.');
      return;
    }
    setUploading(p => ({ ...p, [locale]: true }));
    const form = new FormData();
    form.append('file', file);
    try {
      await axios.post(
        `${API}/admin/landing/locale-video/${locale}`, form,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      toast.success(`Mei intro video set for ${LOCALE_META[locale]?.name ?? locale}.`);
      await load();
    } catch (e) {
      toast.error('Upload failed.', { description: e?.response?.data?.detail });
    }
    setUploading(p => ({ ...p, [locale]: false }));
    if (fileRefs.current[locale]) fileRefs.current[locale].value = '';
  };

  const removeVideo = async (locale) => {
    if (!window.confirm(`Remove video for ${locale}?`)) return;
    await axios.delete(`${API}/admin/landing/locale-video/${locale}`, { withCredentials: true });
    toast.info(`Video removed for ${locale}.`);
    await load();
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-6 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading localization data…
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4" data-testid="landing-localization-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold flex items-center gap-2">
            <Globe className="h-4 w-4 text-primary" />
            Landing Page Regionalization
          </h2>
          <p className="mt-0.5 text-[12px] text-muted-foreground">
            Claude Haiku generates culturally-adapted copy for each locale.
            Mei intro videos are managed per locale via Emergent Object Storage.
          </p>
        </div>
        <Button
          size="sm"
          onClick={regenerateAll}
          disabled={generatingAll}
          data-testid="regen-all-btn"
          className="gap-1.5"
        >
          {generatingAll
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <RefreshCw className="h-3.5 w-3.5" />}
          {generatingAll ? 'Generating all…' : 'Regenerate all 5 locales'}
        </Button>
      </div>

      {/* Locale rows */}
      <div className="divide-y divide-border rounded-lg border">
        {locales.map((row) => {
          const meta = LOCALE_META[row.locale] ?? { flag: '🌐', name: row.locale, note: '' };
          const sc   = STATUS_CONFIG[row.status] ?? STATUS_CONFIG.not_generated;
          const vid  = videos[row.locale];
          const isEn = row.locale === 'en';
          const isExpanded = !!expanded[row.locale];
          return (
            <div key={row.locale} className="bg-card">
              {/* Locale header row */}
              <div className="flex items-center gap-3 px-4 py-3">
                <span className="text-xl">{meta.flag}</span>
                <div className="flex-1">
                  <p className="text-[13px] font-semibold">{meta.name}</p>
                  <p className="text-[11px] text-muted-foreground">{meta.note}</p>
                </div>
                <Badge variant="outline" className={cn('text-[10.5px]', sc.color)}>
                  {sc.label}
                </Badge>
                {row.generated_at && (
                  <span className="text-[10px] text-muted-foreground">
                    {new Date(row.generated_at).toLocaleDateString()}
                  </span>
                )}
                {!isEn && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 gap-1 px-2 text-[11px]"
                      onClick={() => regenerate(row.locale)}
                      disabled={!!generating[row.locale]}
                      data-testid={`regen-btn-${row.locale}`}
                    >
                      {generating[row.locale]
                        ? <Loader2 className="h-3 w-3 animate-spin" />
                        : <RefreshCw className="h-3 w-3" />}
                      {generating[row.locale] ? 'Generating…' : 'Regenerate'}
                    </Button>
                    {row.status === 'published' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-destructive/70 hover:text-destructive"
                        onClick={() => deleteLocale(row.locale)}
                        title="Clear this locale's regionalized content"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0"
                      onClick={() => toggleExpand(row.locale)}
                      title={isExpanded ? 'Collapse preview' : 'Expand preview'}
                      data-testid={`expand-btn-${row.locale}`}
                    >
                      {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                    </Button>
                  </>
                )}
              </div>

              {/* Expandable preview */}
              {isExpanded && !isEn && (
                <div className="border-t bg-muted/30 px-4 py-3 space-y-2" data-testid={`preview-${row.locale}`}>
                  {Object.keys(preview[row.locale] ?? {}).length === 0
                    ? <p className="text-[11.5px] text-muted-foreground italic">No regionalized content yet — click Regenerate to generate it.</p>
                    : Object.entries(preview[row.locale] ?? {}).map(([k, v]) => (
                        <div key={k} className="grid grid-cols-[180px_1fr] gap-2 text-[11.5px]">
                          <span className="font-mono text-muted-foreground truncate">{k}</span>
                          <span>{v}</span>
                        </div>
                      ))
                  }
                </div>
              )}

              {/* Mei video section */}
              <div className="flex items-center gap-3 border-t bg-muted/20 px-4 py-2.5">
                <span className="text-[11px] font-medium text-muted-foreground w-24 shrink-0">Mei intro video</span>
                {vid?.cdn_url ? (
                  <div className="flex flex-1 items-center gap-2">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                    <span className="text-[11px] text-muted-foreground truncate max-w-[200px]">{vid.filename}</span>
                    <span className="text-[10px] text-muted-foreground">
                      ({vid.size ? `${(vid.size / 1024 / 1024).toFixed(1)} MB` : '?'})
                    </span>
                    <Button
                      size="sm" variant="ghost"
                      className="h-6 w-6 p-0 text-destructive/70 hover:text-destructive"
                      onClick={() => removeVideo(row.locale)}
                      title="Remove video"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <span className="flex-1 text-[11px] text-muted-foreground italic">Not yet uploaded</span>
                )}
                <div className="shrink-0">
                  <input
                    ref={el => { fileRefs.current[row.locale] = el; }}
                    type="file"
                    accept="video/mp4,video/webm,video/quicktime"
                    className="hidden"
                    id={`vid-upload-${row.locale}`}
                    onChange={e => uploadVideo(row.locale, e.target.files?.[0])}
                  />
                  <Button
                    size="sm" variant="outline"
                    className="h-7 gap-1 px-2 text-[11px]"
                    disabled={!!uploading[row.locale]}
                    onClick={() => document.getElementById(`vid-upload-${row.locale}`)?.click()}
                    data-testid={`upload-video-btn-${row.locale}`}
                  >
                    {uploading[row.locale]
                      ? <Loader2 className="h-3 w-3 animate-spin" />
                      : <Upload className="h-3 w-3" />}
                    {uploading[row.locale] ? 'Uploading…' : vid?.cdn_url ? 'Replace' : 'Upload MP4'}
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-[11px] text-muted-foreground/70">
        Regionalization uses <strong>Claude Haiku 4.5</strong> with per-locale cultural briefs.
        Videos are stored via Emergent Object Storage and served from CDN.
        Language detection order: URL prefix → localStorage → browser language.
      </p>
    </div>
  );
}
